import {
  createVisibilityMountState,
  normalizeVisibilityHistory,
  reconcileVisibilityHistory,
  recordVisibilityView,
  type VisibilityHistory,
} from "./visibilityPolicy";
import {
  normalizeTimerAnswerState,
  type TimerAnswerState,
} from "./timer";

export const CONDITIONAL_VISIBILITY_STATE_VERSION = 3 as const;

export interface ConditionalVisibilityActiveReveal {
  id: string;
  timerBlockId: string;
  startedAt: string;
}

export interface ConditionalVisibilityResetEvent {
  id: string;
  timerBlockId: string | null;
  resetAt: string;
  completedAt: string | null;
  status: "pending" | "completed";
  previousHistory: VisibilityHistory;
  previousActiveReveal: ConditionalVisibilityActiveReveal | null;
  previousTimerState: unknown;
  replacementTimerState: TimerAnswerState | null;
}

export interface ConditionalVisibilityState {
  version: typeof CONDITIONAL_VISIBILITY_STATE_VERSION;
  history: VisibilityHistory;
  activeReveal: ConditionalVisibilityActiveReveal | null;
  resetCount: number;
  resetHistory: ConditionalVisibilityResetEvent[];
  pendingResetId: string | null;
  updatedAt: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function timestamp(value: unknown, fallback: number): string {
  if (typeof value === "string" && Number.isFinite(Date.parse(value))) {
    return value;
  }
  return new Date(fallback).toISOString();
}

function nonNegativeInteger(value: unknown): number {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : 0;
}

function normalizeActiveReveal(
  value: unknown,
): ConditionalVisibilityActiveReveal | null {
  if (!isRecord(value)) return null;
  const id = typeof value.id === "string" ? value.id.trim() : "";
  const timerBlockId = typeof value.timerBlockId === "string"
    ? value.timerBlockId.trim()
    : "";
  if (!id || !timerBlockId) return null;
  return {
    id,
    timerBlockId,
    startedAt: timestamp(value.startedAt, 0),
  };
}

function normalizeResetEvent(
  value: unknown,
): ConditionalVisibilityResetEvent | null {
  if (!isRecord(value)) return null;
  const id = typeof value.id === "string" ? value.id.trim() : "";
  const timerBlockId = typeof value.timerBlockId === "string"
    ? value.timerBlockId.trim()
    : null;
  const replacementTimerState = normalizeTimerAnswerState(
    value.replacementTimerState,
  );
  if (!id || (timerBlockId && !replacementTimerState)) {
    return null;
  }
  const status = value.status === "completed" ? "completed" : "pending";
  return {
    id,
    timerBlockId: timerBlockId || null,
    resetAt: timestamp(value.resetAt, 0),
    completedAt: status === "completed"
      ? timestamp(value.completedAt, 0)
      : null,
    status,
    previousHistory: normalizeVisibilityHistory(value.previousHistory),
    previousActiveReveal: normalizeActiveReveal(value.previousActiveReveal),
    previousTimerState: value.previousTimerState ?? null,
    replacementTimerState,
  };
}

export function conditionalVisibilityStateLocator(
  dataPath: string[],
  blockId: string,
): { dataPath: string[]; key: string } {
  return {
    dataPath,
    key: conditionalVisibilityStateKey(blockId),
  };
}

export function conditionalVisibilityStateKey(blockId: string): string {
  return `conditional-visibility.${blockId}`;
}

export function normalizeConditionalVisibilityState(
  value: unknown,
  policy: unknown,
  nowMs = Date.now(),
): ConditionalVisibilityState {
  const record = isRecord(value) ? value : {};
  const resetHistory = Array.isArray(record.resetHistory)
    ? record.resetHistory
      .map((event) => normalizeResetEvent(event))
      .filter((event): event is ConditionalVisibilityResetEvent => event !== null)
    : [];
  const pendingResetId = typeof record.pendingResetId === "string"
    && resetHistory.some(
      (event) => event.id === record.pendingResetId && event.status === "pending",
    )
    ? record.pendingResetId
    : resetHistory.find((event) => event.status === "pending")?.id ?? null;

  return {
    version: CONDITIONAL_VISIBILITY_STATE_VERSION,
    history: reconcileVisibilityHistory(record.history, policy, { now: nowMs }),
    activeReveal: normalizeActiveReveal(record.activeReveal),
    resetCount: Math.max(nonNegativeInteger(record.resetCount), resetHistory.length),
    resetHistory,
    pendingResetId,
    updatedAt: timestamp(record.updatedAt, nowMs),
  };
}

export function updateConditionalVisibilityState(
  state: ConditionalVisibilityState,
  patch: Partial<
    Pick<
      ConditionalVisibilityState,
      "history" | "activeReveal" | "resetCount" | "resetHistory" | "pendingResetId"
    >
  >,
  nowMs = Date.now(),
): ConditionalVisibilityState {
  return {
    ...state,
    ...patch,
    updatedAt: new Date(nowMs).toISOString(),
  };
}

export function beginConditionalVisibilityReset(
  state: ConditionalVisibilityState,
  options: {
    timerBlockId?: string | null;
    policy: unknown;
    previousTimerState: unknown;
    replacementTimerState?: TimerAnswerState | null;
    maximumResets: number;
    resetId: string;
    nowMs?: number;
  },
): ConditionalVisibilityState | null {
  const maximumResets = Math.max(0, Math.floor(options.maximumResets));
  if (
    !options.resetId
    || state.pendingResetId
    || state.resetCount >= maximumResets
  ) {
    return null;
  }

  const nowMs = options.nowMs ?? Date.now();
  const resetEvent: ConditionalVisibilityResetEvent = {
    id: options.resetId,
    timerBlockId: options.timerBlockId || null,
    resetAt: new Date(nowMs).toISOString(),
    completedAt: null,
    status: "pending",
    previousHistory: state.history,
    previousActiveReveal: state.activeReveal,
    previousTimerState: options.previousTimerState ?? null,
    replacementTimerState: options.replacementTimerState ?? null,
  };

  return updateConditionalVisibilityState(
    state,
    {
      history: reconcileVisibilityHistory(null, options.policy, {
        now: nowMs,
      }),
      activeReveal: null,
      resetCount: state.resetCount + 1,
      resetHistory: [...state.resetHistory, resetEvent],
      pendingResetId: resetEvent.id,
    },
    nowMs,
  );
}

export function completeConditionalVisibilityReset(
  state: ConditionalVisibilityState,
  resetId: string,
  policy: unknown,
  nowMs = Date.now(),
): ConditionalVisibilityState {
  const resetEvent = state.resetHistory.find((event) => event.id === resetId);
  if (!resetEvent || resetEvent.status === "completed") return state;
  const completedAt = new Date(nowMs).toISOString();
  const recorded = recordVisibilityView(
    policy,
    state.history,
    createVisibilityMountState(resetEvent.id),
    nowMs,
  );
  return updateConditionalVisibilityState(
    state,
    {
      history: recorded.history,
      activeReveal: resetEvent.timerBlockId && resetEvent.replacementTimerState
        ? {
            id: resetEvent.id,
            timerBlockId: resetEvent.timerBlockId,
            startedAt: resetEvent.replacementTimerState.lastPersistedAt,
          }
        : null,
      resetHistory: state.resetHistory.map((event) =>
        event.id === resetId
          ? { ...event, status: "completed", completedAt }
          : event
      ),
      pendingResetId: state.pendingResetId === resetId
        ? null
        : state.pendingResetId,
    },
    nowMs,
  );
}

export function getPendingConditionalVisibilityReset(
  state: ConditionalVisibilityState,
): ConditionalVisibilityResetEvent | null {
  if (!state.pendingResetId) return null;
  return state.resetHistory.find(
    (event) => event.id === state.pendingResetId && event.status === "pending",
  ) ?? null;
}
